import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Transition } from "react-transition-group";
import ModalContainer from "../ModalContainer";
import { useWizardModal, WizardModalContextProvider } from "./context";
import { animationClassHandler } from "../../../helper/common.helper";
import { cn } from "../../../lib/utils";

const WizardModal = forwardRef(({ children, initialSteps = [], ...props }, ref) => {
	return (
		<WizardModalContextProvider initialSteps={initialSteps}>
			<WizardModalContent ref={ref} {...props}>
				{children}
			</WizardModalContent>
		</WizardModalContextProvider>
	);
});

const WizardModalContent = forwardRef(
	({ open, setOpen, disableClose, children, className, stepClassName = {} }, ref) => {
		const hook = useWizardModal();

		useImperativeHandle(
			ref,
			() => ({
				...hook,
			}),
			[hook.wizardStep, hook.wizardVisible]
		);

		const { resetWizard, wizardStep } = hook;

		useEffect(() => {
			if (open) {
				resetWizard();
			}
		}, [open]);

		return (
			<ModalContainer isOpen={open} setIsOpen={setOpen} disableClose={disableClose}>
				<div className={cn("main_modal_content", className, stepClassName[wizardStep])}>
					<button
						className="main_close"
						onClick={() => !disableClose && setOpen(false)}
						type="button"
						aria-label="Chiudi"
					>
						<span className="icon_10 default close icon_mask !bg-white"></span>
					</button>
					{children}
				</div>
			</ModalContainer>
		);
	}
);

export const WizardHeader = ({
	title,
	description,
	descriptionContent,
	showBackButton = false,
	rightContent,
	className,
}) => {
	const { currentStep, goToPrevStep, wizardVisible } = useWizardModal();

	return (
		<div
			className={cn(
				"main_header_modal flex_header transition-opacity",
				wizardVisible === 0 && "opacity-0",
				className
			)}
		>
			<div className="left_content">
				{showBackButton && (
					<button
						type="button"
						className="main_back_button"
						onClick={goToPrevStep}
						aria-label="Torna al passaggio precedente"
					>
						<span className="icon_16 default arrow left icon_mask !bg-interactive-100"></span>
					</button>
				)}

				<div className="main_desc">
					<span className="title">{title || currentStep?.title}</span>
					{description && <div className="desc">{description}</div>}
					{descriptionContent}
				</div>
			</div>

			{rightContent && <div className="right_content">{rightContent}</div>}
		</div>
	);
};

export const WizardBody = ({ stepIndex, children, className, transitionProps }) => {
	const { wizardVisible, goToPrevStep } = useWizardModal();
	const stepRef = useRef(null);

	const isCurrentStep = stepIndex === wizardVisible;

	return (
		<Transition
			nodeRef={stepRef}
			in={isCurrentStep}
			onExited={goToPrevStep}
			onEnter={transitionProps?.onEnter}
			mountOnEnter
			unmountOnExit
			timeout={{ enter: 250, exit: 250 }}
		>
			{(state) => (
				<div ref={stepRef} className={cn("main_body_modal", className)}>
					<div className={animationClassHandler(state, "wizard_step_in", "wizard_step_out")}>
						{children}
					</div>
				</div>
			)}
		</Transition>
	);
};

export const WizardStepContent = ({ stepIndex, children }) => {
	const { wizardStep, setWizardVisible } = useWizardModal();
	const stepRef = useRef(null);

	const isCurrentStep = stepIndex === wizardStep;

	return (
		<Transition
			nodeRef={stepRef}
			in={isCurrentStep}
			onEntered={() => {
				setWizardVisible(wizardStep);
			}}
			mountOnEnter
			unmountOnExit
			timeout={{ enter: 0, exit: 0 }}
		>
			{(state) => children}
		</Transition>
	);
};

export const WizardFooter = ({
	children,
	nextButtonText = "Procedi",
	showNextButton = true,
	onNext,
	disableNext = false,
	isLoading = false,
	buttonProps = {},
	enableHandler = true,
	leftContent,
	className,
}) => {
	const { goToNextStep, wizardVisible } = useWizardModal();

	const handleNext = () => {
		if (onNext) {
			onNext();
		} else {
			goToNextStep();
		}
	};

	return (
		<div className={cn("main_footer_modal", className)}>
			{leftContent && <div className="left_content">{leftContent}</div>}
			<div className="right_content">
				{children ||
					(showNextButton && (
						<div className={cn("transition-opacity", wizardVisible === 0 && "opacity-0")}>
							<button
								onClick={() => enableHandler && handleNext()}
								type="button"
								disabled={disableNext}
								{...buttonProps}
								className={cn(
									"button fill success",
									disableNext && "disabled",
									isLoading && "loading",
									buttonProps.className
								)}
							>
								<div className="load_content">{nextButtonText}</div>
								{isLoading && <div className="main_loader_icon fade_in"></div>}
							</button>
						</div>
					))}
			</div>
		</div>
	);
};

export default WizardModal;
